import { GenericModelApi, FetchOpts } from "./GenericModelApi";
import { ApiClient, ApiError, ApiSchema, FetchResponse } from "./ApiClient";
import { getTenantId } from "@utils/Auth";
import { omitProperties } from "@utils/Utils";
import { DocumentSequenceApi } from "./DocumentSequenceApi";
import { DocumentAutoGeneratedNumberApi } from "./DocumentAutoGeneratedNumberApi";

import { matchesAmplifyFilter } from "@utils/Api";
import { BuyInvoiceFormData, buyInvoiceRelatedAttributes } from "@/formTypes/BuyInvoiceDataType";
import { BuyInvoiceFormDataKeys } from "@/generated/BuyInvoiceFormDataKeys";
import { BuyInvoiceProductApi, BuyInvoiceProductDataType } from "./BuyInvoiceProductApi";
import { BuyInvoicePaymentApi, BuyInvoicePaymentDataType } from "./BuyInvoicePaymentApi";
import { saveEDocumentViaLambda } from "./EDocumentSaveApi";

export type BuyInvoiceDataType = Partial<ApiSchema["BuyInvoice"]["type"] & BuyInvoiceFormData>;

class Api extends GenericModelApi<"BuyInvoice", BuyInvoiceDataType> {
    protected userSortKeyDateField = "fecha" as const

    async fetchAll(filters: any = {}, token?: string, limit: number = 20, opts: FetchOpts = {}): Promise<FetchResponse<BuyInvoiceDataType>> {
        const selectionSet = [
            ...BuyInvoiceFormDataKeys.filter(k => !buyInvoiceRelatedAttributes.includes(k)),
            "currency.*",
        ]

        return super.fetchAll(filters, token, limit, selectionSet, opts);
    }

    async fetchAllForStatus(providerId: string, statusFilter: string, buyInvoiceType: BuyInvoiceType, filters: any = {}, token?: string, limit: number = 1000): Promise<FetchResponse<BuyInvoiceDataType>> {
        const selectionSet = [
            ...BuyInvoiceFormDataKeys.filter(k => !buyInvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "buyInvoiceProducts.*"
        ]
        const tenantId = await getTenantId();
        const fetchFunction = ApiClient.models[this.modelName].listBuyInvoiceByStatusClientTenant;

        const collected: BuyInvoiceDataType[] = [];
        let nextToken: string | undefined = token;

        while (collected.length < limit) {
            const { data, errors, nextToken: nt } = await fetchFunction({
                owner: tenantId,
                buyInvoiceTypeStatusProviderId: {
                    beginsWith: {
                        buyInvoiceType: buyInvoiceType,
                        status: statusFilter,
                        providerId: providerId
                    }
                }
            }, {
                sortDirection: "ASC",
                limit: limit,
                nextToken,
                selectionSet: selectionSet,
            });

            if (errors) {
                throw new ApiError("Error al obtener facturas de Compra por tenant", "API_ERROR", errors);
            }

            const pageItems = (data || []).filter((item: BuyInvoiceDataType) => matchesAmplifyFilter(item, filters));
            collected.push(...pageItems);

            if (!nt) {
                nextToken = undefined;
                break;
            }
            nextToken = nt;
        }

        return { items: collected, nextToken };

    }

    async fetchAllByRecibe(recibeId: string): Promise<FetchResponse<BuyInvoiceDataType>> {
        const fetchFunction = ApiClient.models[this.modelName].listBuyInvoiceByRecibeId;
        const selectionSet = [
            ...BuyInvoiceFormDataKeys.filter(k => !buyInvoiceRelatedAttributes.includes(k)),
            "currency.*",
        ];

        const { data, errors, nextToken: nt } = await fetchFunction({
            providerRecibeId: recibeId
        }, {
            limit: 1000,
            selectionSet,
        });

        if (errors) {
            throw new ApiError("Error al obtener Facturas de compra por recibo", "API_ERROR", errors);
        }

        return { items: data };
    }

    async findByNumero(numero: number): Promise<BuyInvoiceDataType | undefined> {
        const { items } = await this.fetchAll({ numero: { eq: numero } }, undefined, 20);
        return items.find((item) => item.buyInvoiceType === "Buy");
    }

    async fetchAllByPeriod(startData: string, endData: string, filters: any = {}, token?: string, limit: number = 500): Promise<FetchResponse<BuyInvoiceDataType>> {
        const selectionSet = [
            ...BuyInvoiceFormDataKeys.filter(k => !buyInvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "buyInvoiceProducts.*"
        ]
        return await super.fetchAllByPeriod("listBuyInvoiceByFechaTenant", startData, endData, filters, token, {
            selectionSet,
            limit
        })

    }

    /**
     * Guardado transaccional en una sola llamada (lambda saveEDocument).
     * Las compras no ajustan inventario (stockMode "none").
     */
    async createBuyInvoiceAndProducts(input: BuyInvoiceDataType, products: BuyInvoiceProductDataType[], payments: BuyInvoicePaymentDataType[]): Promise<BuyInvoiceDataType> {
        const documentData = {
            ...input,
            status: input.status || "pendiente",
        }

        const lines = products.map((p) => {
            const product = omitProperties({ ...p }, ["id", "forceRecalculatePrice", "product", "productId", "comision"], true);
            if (product.exemption && typeof product.exemption == "object") {
                product.exemption = JSON.stringify(product.exemption);
            }
            return product;
        })

        const buyInvoicePayments = payments.map((p) => omitProperties({ ...p }, ["id"], true))

        const data = await saveEDocumentViaLambda<BuyInvoiceDataType>({
            documentType: "BuyInvoice",
            document: documentData as Record<string, unknown>,
            lines,
            payments: buyInvoicePayments,
            stockMode: "none",
        })

        this.invalidateAllByTenant()
        this.clearCache()
        BuyInvoiceProductApi.invalidateAllByTenant()
        BuyInvoicePaymentApi.invalidateAllByTenant()
        DocumentSequenceApi.invalidateAllByTenant()
        DocumentAutoGeneratedNumberApi.invalidateAllByTenant()

        return data;
    }
}
const api = new Api("BuyInvoice", "sequence", "DESC")
api.setCreateDataFieldName("fecha")
export const BuyInvoiceApi = api